import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Divider, FormHelperText, Link, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIntl } from 'react-intl';
import { commonMessages } from 'utils/commonMessages';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { ServiceCallsApi } from 'modules/service-call/api';
import { FindServiceCallByIdQuery } from 'modules/service-call/types';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { WhyNoServiceCallDialog } from './WhyNoServiceCallDialog';

interface AccordionWithServiceCallNumberProps {
  expanded: boolean,
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void
}

export const AccordionWithServiceCallNumber: React.FC<AccordionWithServiceCallNumberProps> = ({expanded, onChange}) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [hasNoResult, setHasNoResult] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { register, handleSubmit, clearErrors, formState: { errors }, getValues } = useForm<FindServiceCallByIdQuery>();

  const findMutation = useMutation(ServiceCallsApi.findById, {
    onSuccess: (result) => {
      navigate(`/service-calls/${result}`);
    },
    onError: () => {
      setHasNoResult(true);
    }
  });

  const onSubmit = (data: FindServiceCallByIdQuery) => {
    clearErrors();
    setHasNoResult(false);
    findMutation.mutate(data);
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const isSubmitting = findMutation.isLoading;

  return(
    <Accordion expanded={expanded} onChange={onChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-withservice-call-number-content"
        id="panel-withservice-call-number-header"
      >
        <Typography>{formatMessage({ id: 'FindServiceCall.WithTicket', defaultMessage: 'With your ticket number' })}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack gap={2}>
            <TextField
              {...register('id', { required: formatMessage(commonMessages.fieldIsRequired) })}
              required
              fullWidth
              label={formatMessage(commonMessages.ticketNumber)}
              variant="outlined"
              autoComplete='ticketNumber'
              error={Boolean(errors.id)}
              helperText={errors.id?.message}
              disabled={isSubmitting}
            />
            <Stack>
              <Stack direction='row'>
                <TextField
                  {...register('contractNumber', {
                    validate: (value) => value !== '' || getValues('clientNumber') !== '' ? true : formatMessage({id: 'FindServiceCall.ContractOrClientNumberRequired', defaultMessage: 'Atleast Contract or Client number is required'})
                  })}
                  label={formatMessage(commonMessages.contractNumber)}
                  variant="outlined"
                  autoComplete='contractNumber'
                  error={Boolean(errors.contractNumber)}
                  disabled={isSubmitting}
                />
                <Divider orientation="vertical" flexItem sx={{textTransform:'uppercase'}}>{formatMessage(commonMessages.or)}</Divider>
                <TextField
                  {...register('clientNumber')}
                  label={formatMessage(commonMessages.clientNumber)}
                  variant="outlined"
                  autoComplete='clientNumber'
                  error={Boolean(errors.contractNumber)}
                  disabled={isSubmitting}
                />
              </Stack>
              {errors.contractNumber && (
                <FormHelperText error sx={{mx:2}}>{errors.contractNumber?.message}</FormHelperText>
              )}
            </Stack>
            {hasNoResult && (
              <Stack direction='row' gap={1/2} flexWrap='wrap'>
                <FormHelperText error>{formatMessage({ id: 'FindServiceCall.NoResult', defaultMessage: 'We didn\'t find a service call with the supplied information.' })}</FormHelperText>
                <Link component='button' onClick={handleOpenDialog}>{formatMessage({ id: 'FindServiceCall.NoResultWhy', defaultMessage: 'Find out why' })}</Link>
              </Stack>
            )}
            <Stack direction='row' justifyContent='end'>
              <LoadingButton variant="contained" color='secondary' type='submit' loading={isSubmitting} >{formatMessage({ id: 'FindServiceCall.FindButton', defaultMessage: 'Find' })}</LoadingButton>
            </Stack>
          </Stack>
        </form>
      </AccordionDetails>
      <WhyNoServiceCallDialog isOpen={isDialogOpen} onClose={handleCloseDialog} />
    </Accordion>
  );
};